import { Link } from 'react-router-dom';
import { appKit, kitBuild, dollars, perMonth } from '../data/plans';
import KitIpDiagram from '../components/KitIpDiagram';
import SceneFrame from '../components/three/SceneFrame';
import './AppKitDocs.css';
import PageWorld from '../components/site/PageWorld';
import ProductHandoff from '../components/site/ProductHandoff';
import { APP_KIT } from '../data/worlds';
import { PageFilm } from '../components/PageFilm';
import PitchButton from '../components/PitchButton';

// What orbits an instance — each one is a real fleet feature
// ([[fleet-ops]], [[hosted-apps]]). Same order as the sentries in EcoScene.
// Moved here from the homepage 2026-10-14 ([[homepage-conversion-first]]).
const FLEET = [
  { k: 'Security', v: 'Assurance scans — ZAP baseline, nuclei, TLS — on a monthly schedule; findings tracked until resolved.' },
  { k: 'Monitor', v: 'Health checked from outside and reported from inside, per service; HTTP feature checks come with the rent. Incidents land in the fleet console.' },
  { k: 'Acts on its own', v: 'Restarts a down service. Rolls back a production deploy that starts failing its checks inside the window. Every action is on the record.' },
  { k: 'UAT twin', v: 'A second instance to stage a change, then promote it to prod.' },
  { k: 'Edge', v: 'Your domain, TLS terminated at the edge, one wildcard cert per host.' },
  { k: 'Fleet ops', v: 'Kit updates roll out from the control plane, UAT twins first. The layer that is yours stays yours.' },
];

// Public explainer for the second product. Marketing-safe: the page states
// the stack, the layer boundary, where an instance runs, and that one React
// build is the app on desktop, phone, tablet, and in the store shells.
// Capacitor wraps that build; it is not a Swift or Kotlin rewrite.

const SURFACES = [
  {
    kind: 'desk',
    title: 'Desktop',
    body: 'Full layout in Chrome, Safari, Firefox, or Edge. The address is the domain you bring, or the host that comes with the instance.',
  },
  {
    kind: 'phone',
    title: 'Phone',
    body: 'The same routes, refit for a narrow screen. Field staff open it in the mobile browser. There is no second app to keep in sync.',
  },
  {
    kind: 'tablet',
    title: 'Tablet',
    body: 'The in-between layout: lists stay readable, detail stays beside them. One stylesheet, three widths.',
  },
  {
    kind: 'store',
    title: 'Installed',
    body: 'The same web build wraps in a Capacitor shell for the App Store and Google Play. A store release is a snapshot. The site at your domain updates on every deploy.',
  },
  {
    kind: 'server',
    title: 'The server',
    body: 'One Linux stack behind all of those clients. nventr hosts it, or it runs in your AWS, Azure, Google Cloud, or on-prem account.',
  },
];

// Bring the app you already have — six parts, each a real step the console
// has ([[bring-an-existing-app]], [[roadmap-runner]]). The ids are the
// pitch's proof targets (shared/pitch.js `bring-*`).
const BRING = [
  {
    id: 'bring-repo',
    title: '1 · The repository',
    body: 'Point a GitHub repository at a new instance. We read it and host it in the shape it is: a layer is mirrored, a kit fork contributes the extension files you agree to keep, any other stack comes up as a preview with your repository checked out read-only beside a fresh layer for the Dev Console to port from. The token stays in our vault.',
  },
  {
    id: 'bring-brain',
    title: '2 · The project brain',
    body: 'Attach the roadmap, the open change requests, the specs. They become the first pages of this app\u2019s project brain, beside the README and docs read straight from the repository. NoVA, the Dev Console agents, and the roadmap planner all read from the same pages.',
  },
  {
    id: 'bring-plan',
    title: '3 · The plan',
    body: 'Plan the roadmap: every item becomes a story with acceptance criteria, a priority, the area of the app it touches, and the stories it depends on. The planner orders them into waves \u2014 what can run side by side, what has to wait \u2014 and shows you the plan. You approve it. Nothing is built before that.',
  },
  {
    id: 'bring-run',
    title: '4 · The run',
    body: 'Each wave opens one Dev Console session per story on your instance, in parallel. Each session reads the brain, works your layer repo, opens a pull request, and ships to the UAT twin. When every story in the wave is shipped and the instance\u2019s self-test is green, the next wave starts. Production is still a promote you click.',
  },
  {
    id: 'bring-fleet',
    title: '5 · The fleet',
    body: 'While the waves run, the instance is watched like every other: feature checks, the security baseline, health inside and out, a service restarted when it reports down twice, a deploy rolled back when its checks fail. Each session\u2019s model is chosen by the routing policy, under a cap, on your keys if you bring them.',
  },
  {
    id: 'bring-cutover',
    title: '6 · The cutover',
    body: 'The site you run today keeps serving and is watched on its own. When the layer holds the app, an admin cuts the domain over \u2014 the apex you already use, DNS when the page says so. Your database does not move with it.',
  },
];

const INSIDE = [
  {
    kind: 'web',
    title: 'The web app',
    body: 'A React application your users sign into. Desktop, phone, and tablet share the routes. Auth is the session on the instance.',
  },
  {
    kind: 'api',
    title: 'API and workers',
    body: 'A Node API plus claim-based workers. A job is a document. A worker claims it, does the work, and writes the result back. Two tasks can overlap during a deploy without double-processing.',
  },
  {
    kind: 'db',
    title: 'Database',
    body: 'Postgres with sign-in, realtime, and row-level security. The data plane lives inside the instance, with the app.',
  },
  {
    kind: 'files',
    title: 'Files',
    body: 'S3-compatible object storage. Browsers upload large files straight to the bucket. Playback and download links are presigned.',
  },
  {
    kind: 'dev',
    title: 'Dev Console',
    body: 'Included with the Kit rent. The loop above — ask, review, deploy — is this console, at /dev. The other cards are what your users open.',
  },
  {
    kind: 'know',
    title: 'Knowledge and agents',
    body: 'Business knowledge stays in the database and is retrieved at answer time. On a kit instance, nQube runs inside this app. Org-wide pipelines into systems you already use are Platform.',
  },
  {
    kind: 'mcp',
    title: 'MCP server',
    body: 'Every instance is an MCP server. Cursor, Claude and ChatGPT connect at /api/mcp with a token or a sign-in and get the same record tools the in-app assistant has, under each member\'s own seat, every call audited.',
  },
];

function Sketch({ kind }) {
  if (kind === 'web') {
    return (
      <div className="ak-sketch" aria-hidden="true">
        <div className="ak-sk-chrome"><i /><i /><i /><em>northline.app</em></div>
        <div className="ak-sk-split"><b /><span /></div>
      </div>
    );
  }
  if (kind === 'api') {
    return (
      <div className="ak-sketch ak-sketch-mono" aria-hidden="true">
        <em>claim job · harbor-crane</em>
        <em className="ok">worker-3 · wrote result</em>
        <em>heartbeat · 12s</em>
      </div>
    );
  }
  if (kind === 'db') {
    return (
      <div className="ak-sketch" aria-hidden="true">
        <div className="ak-sk-table"><b>job</b><b>when</b></div>
        <div className="ak-sk-table"><span>harbor</span><span>14:20</span></div>
        <div className="ak-sk-table"><span>pier 4</span><span>15:05</span></div>
      </div>
    );
  }
  if (kind === 'files') {
    return (
      <div className="ak-sketch ak-sketch-files" aria-hidden="true">
        <div><b>manifest.pdf</b><span>2.4 MB</span></div>
        <div><b>gate-photo.jpg</b><span>8.1 MB</span></div>
      </div>
    );
  }
  if (kind === 'dev') {
    return (
      <div className="ak-sketch ak-sketch-diff" aria-hidden="true">
        <em className="add">+ gate holds until review</em>
        <em className="del">− post straight to the ledger</em>
        <em>layer/ext/gate.js</em>
      </div>
    );
  }
  if (kind === 'mcp') {
    return (
      <div className="ak-sketch ak-sketch-mono" aria-hidden="true">
        <em>tools/list · 33 tools · scope read</em>
        <em className="ok">find_job · "harbor crane" → 1 record</em>
        <em>audit · mcp · allowed</em>
      </div>
    );
  }
  return (
    <div className="ak-sketch ak-sketch-know" aria-hidden="true">
      <span>Harbor crane?</span>
      <b>Gate B · crew of 4 · 14:20</b>
    </div>
  );
}

function DeviceStage() {
  return (
    <div className="ak-stage" aria-hidden="true">
      <div className="ak-desk">
        <div className="ak-chrome">
          <div className="ak-dots"><i /><i /><i /></div>
          <div className="ak-url">northline.demo</div>
        </div>
        <div className="ak-app">
          <div className="ak-app-bar">
            <div className="ak-brand">Northline</div>
            <div className="ak-live">Live</div>
          </div>
          <div>
            <div className="ak-job"><strong>Harbor crane</strong><span>14:20</span></div>
            <div className="ak-job"><strong>Pier 4 intake</strong><span>15:05</span></div>
            <div className="ak-job"><strong>Yard check</strong><span>16:40</span></div>
          </div>
          <div className="ak-detail">
            <b>Harbor crane</b>
            Crew of 4 · gate B
            <div className="ak-meter"><i /></div>
            <small>Same record, open on the phone.</small>
          </div>
        </div>
      </div>
      <div className="ak-phone">
        <div className="ak-phone-screen">
          <div className="ak-status"><span>9:41</span><span>5G</span></div>
          <div className="ak-brand">Northline</div>
          <div className="ak-job"><strong>Harbor crane</strong><span>14:20</span></div>
          <div className="ak-job"><strong>Pier 4 intake</strong><span>15:05</span></div>
          <div className="ak-job"><strong>Yard check</strong><span>16:40</span></div>
          <div className="ak-phone-nav"><i className="on" /><i /><i /></div>
        </div>
      </div>
      <p className="ak-stage-cap">One build · desktop and phone</p>
    </div>
  );
}

export default function AppKitDocs() {
  return (
    <PageWorld world={APP_KIT}>
    <div className="cs-store ak">
      <header className="cs-store-hero ak-hero">
        <div>
          <p className="ak-kicker">nventr App Kit · {perMonth(appKit)}</p>
          <h1>The app for building apps</h1>
          <p className="ak-lead">
            Your URL, database and layer repo. Dev Console included. That is
            the second product: the app your users sign into, on a
            foundation we keep running. Dev Console is how you change it:
            ask, review the diff, deploy. People open it on desktop and
            phone in the browser.
          </p>
          <div className="cs-land-cta-row">
            <Link to={appKit.href} className="cs-btn cs-btn-primary">{appKit.cta}</Link>
            <Link to="/platform/dev-console" className="cs-btn cs-btn-ghost">See the console</Link>
          </div>
          <ul className="ak-facts">
            <li>Dev Console included with the rent</li>
            <li>Large files, fan-out jobs, health-gated deploys</li>
            <li>Built-in MCP server for Cursor, Claude and ChatGPT</li>
            <li>License from {perMonth(appKit)} · host included</li>
          </ul>
        </div>
        <DeviceStage />
      </header>

      <PageFilm
        base="/media/app-kit-film"
        prefix="appkit"
        title="App Kit — a foundation and a layer"
        summary="The foundation is ours, the layer is yours. Design it in the Lab, claim the instance, change it through Dev Console with you as the gate, let the design catch up from the build, and the fleet keeps it up. The instance has its own brain and answers over MCP. The times on screen were measured on our own fleet."
        chapters={[
          { at: 0, title: 'Foundation and layer' },
          { at: 9.92, title: 'The Lab' },
          { at: 35.58, title: 'Change it' },
          { at: 60.33, title: 'Keep it up' },
          { at: 82.58, title: 'Its own brain' },
        ]}
      />
      <PitchButton focus="app-kit" hint="Or hear it from NoVA and interrupt with questions." />

      <figure className="ak-weave">
        <img
          src="/art/modules/app-builder.png"
          alt="App Builder after ship: the status strip (health, costs, reviews, release), the app on web and phone, and the Operate column with what needs attention and the app map"
        />
        <figcaption>
          The studio owns the app after ship: Lab, then Ready to ship, then
          Operate. You change your layer in Dev Console. A workflow that leaves
          the app and writes to a system you already use is Platform.
        </figcaption>
      </figure>

      <section className="ak-section" id="beside-platform">
        <h2>App Kit beside Platform</h2>
        <ProductHandoff here="kit">
          <Link to="/platform" className="cs-btn cs-btn-ghost">How Platform works →</Link>
          <Link to="/pricing" className="cs-btn cs-btn-ghost">Pricing →</Link>
        </ProductHandoff>
      </section>

      <section className="ak-section" id="dev-console">
        <h2>What the rent unlocks</h2>
        <p className="ak-intro">
          Dev Console is not a separate product you add later. It is the
          build loop on the instance. Platform does not include it. Kit Site
          at {dollars(kitBuild)} is a public site and does not include it either. Kit does.
        </p>
        <div className="ak-grid ak-grid-4">
          <article className="ak-card">
            <h3>Ask</h3>
            <p>Describe the change. The agent works your layer only. The kit you rent stays read-only, so a commit cannot contain kit code.</p>
          </article>
          <article className="ak-card">
            <h3>Review</h3>
            <p>You read the diff before anything merges. Nothing from the agent ships unseen.</p>
          </article>
          <article className="ak-card">
            <h3>Prove</h3>
            <p>A verifier agent drives the change in a real browser and pins its verdict to the commit, screenshots attached. A review graph adds a judge from another model family before the merge.</p>
          </article>
          <article className="ak-card">
            <h3>Deploy</h3>
            <p>Ship to a preview, then to the live app. Your users keep the same URL. The foundation you rent is unchanged.</p>
          </article>
        </div>
        <p className="ak-intro">
          How far it goes alone is one setting: confirm, verify, autopilot. At confirm every step is your click.
          At verify, routines open sessions from causes on their own — a failing self-test, a thumbs-down, a report
          from the control plane — and every pull request is reviewed before you ship. The default is confirm.
        </p>
      </section>

      <section className="ak-section" id="foundation">
        <h2>What the fee is paying for</h2>
        <p className="ak-intro">
          The license is a foundation already built for large files, heavy
          jobs, and a signed-in product, then watched, hardened, and updated.
          Your layer sits on top of it. Dev Console changes that layer.
          Standing the same path up yourself means paying to rebuild the
          upload path, the health-gated deploy, and the security boundary,
          then living with the gaps that work has already closed.
        </p>
        <div className="ak-grid ak-grid-2">
          <article className="ak-card">
            <h3>Built for the load</h3>
            <p>
              Browsers send large files straight to storage, in parallel
              parts, and play them back from signed links. Heavy jobs fan
              out across short-lived tasks so the request path stays short.
              A public site, a media-heavy product, and a signed-in operation
              share that path. Kit Business and Kit Enterprise add SAML, more
              capacity, and more instances. They run the same foundation.
            </p>
          </article>
          <article className="ak-card">
            <h3>Health on every deploy</h3>
            <p>
              A deploy stays dark until the new task passes its health check.
              The task already serving keeps the app up until then, so work
              in flight is not dropped. After launch the instance keeps
              reporting health.
            </p>
          </article>
          <article className="ak-card">
            <h3>Hardened where it ships</h3>
            <p>
              Database access is scoped to the signed-in user. Secrets resolve
              on the server — not in the page, not in git. A change that steps
              outside your layer fails the build. You read the diff before it
              deploys.
            </p>
          </article>
          <article className="ak-card">
            <h3>Updated with the rent</h3>
            <p>
              Patches and foundation updates ship on the kit you rent. They
              do not commit into your repo. Your pages, workers, and data
              stay the layer you own, and the layer you can export.
            </p>
          </article>
        </div>
        <p className="ak-intro">
          That standing work is inside the license. Dev Console is how you
          change the app on top of it — ask, review, deploy — without
          rebuilding the platform underneath each time the product needs a change.
        </p>
      </section>

      <section className="ak-section" id="assurance">
        <h2>Health, feature checks, and security</h2>
        <p className="ak-intro">
          The instance is watched in three layers. Service health and HTTP
          feature checks come with the Kit rent. Browser flows and the
          monthly security baseline are the Assurance add-on. Talk to us
          for that add-on. Kit Site at {dollars(kitBuild)} does not include any of this.
        </p>
        <div className="ak-grid ak-grid-4">
          <article className="ak-card">
            <h3>Service health</h3>
            <p>
              Included. We probe the app and its health URL from outside,
              the instance reports each of its services from inside, and an
              incident opens when either side fails. Uptime and latency sit
              on the instance.
            </p>
          </article>
          <article className="ak-card">
            <h3>Acts on its own</h3>
            <p>
              A service that reports down twice in a row is restarted, with
              a cooldown and a daily cap. A production deploy whose checks
              start failing inside the window is rolled back to the previous
              build. Both appear under &ldquo;Acted on its own&rdquo; on the
              instance, and in the audit log.
            </p>
          </article>
          <article className="ak-card">
            <h3>Feature checks</h3>
            <p>
              A path can answer 200 while the feature behind it is broken.
              An HTTP check, included with Kit, looks for the status and the
              text you name, on a schedule, and only on your app. A browser
              check opens that page and waits for a selector. Browser checks
              are the Assurance add-on.
            </p>
          </article>
          <article className="ak-card">
            <h3>Security checks</h3>
            <p>
              Assurance runs a monthly passive baseline — spider and passive
              rules, template checks, and the TLS handshake — and shows open
              finding counts on the instance. A deeper active test is a
              confirmed nventr engagement. It is not a button that fires
              attack payloads on a timer. Kit updates stay the rent. The
              scan is what the public surface still exposes.
            </p>
          </article>
        </div>
      </section>

      <section className="ak-section" id="fleet">
        <h2>An instance is spawned into a fleet, not left on a server</h2>
        <p className="ak-intro">
          Every instance gets the same sentries: health checked inside and
          out, security scans, feature checks, an edge with TLS, updates
          from the control plane, a UAT twin when you want one &mdash; and a
          fleet that restarts a down service or rolls back a bad deploy
          before you have read the page.
        </p>
        <SceneFrame
          scene="eco"
          poster="/art/hero3d/eco-poster.jpg"
          alt="An App Kit instance at the center wearing its Command View instruments — cost arc, uptime ticks, usage, deploys, findings — orbited by security, monitoring, edge, and fleet operations from the nventr control plane."
          aspect="840 / 430"
          className="cs-scene-section"
        />
        <ul className="cs-land-eco-row">
          {FLEET.map((e) => (
            <li key={e.k}><strong>{e.k}</strong><span>{e.v}</span></li>
          ))}
        </ul>
      </section>

      <section className="ak-section" id="bring">
        <h2>Bring the app you already have</h2>
        <p className="ak-intro">
          You have an app, a repository, a roadmap, and a backlog of change
          requests. Point the repository at a new instance and nventr hosts
          it in the shape it actually is. Then the work you already planned
          runs here: the roadmap becomes stories with their dependencies, the
          stories run as Dev Console builds in waves, and the fleet watches
          every one of them. Your database, users, and files stay where they are.
        </p>
        <PitchButton focus="bring-your-app" label="Let NoVA pitch bringing your app" hint="Six parts; interrupt with your own repository." />
        <div className="ak-grid ak-grid-3">
          {BRING.map((b) => (
            <article key={b.id} id={b.id} className="ak-card">
              <h3>{b.title}</h3>
              <p>{b.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="ak-section">
        <h2>Open it anywhere</h2>
        <p className="ak-intro">
          The kit ships one React application. Width changes the layout.
          A home-screen icon, when you want one, is that same build inside
          a store shell. Watch faces, widgets, and background audio stay
          outside this path — those are native extras, later, if a product needs them.
        </p>
        <div className="ak-grid ak-grid-5">
          {SURFACES.map((s) => (
            <article key={s.title} className="ak-card">
              <div className={`ak-mark ak-mark-${s.kind}`} aria-hidden="true" />
              <h3>{s.title}</h3>
              <p>{s.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="ak-section">
        <h2>What the instance contains</h2>
        <p className="ak-intro">
          A running product, assembled. You extend it at the seams — backend
          extensions, frontend extensions, and the entity registry — and the
          kit stays the platform underneath.
        </p>
        <div className="ak-grid ak-grid-3">
          {INSIDE.map((item) => (
            <article key={item.title} id={item.kind === 'mcp' ? 'mcp' : undefined} className="ak-card">
              <Sketch kind={item.kind} />
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="ak-section">
        <h2>Your code and the platform code</h2>
        <p className="ak-intro">
          You own everything you build, and you can leave with it. The export
          is a git bundle of your layer: pages, workers, entities, history,
          and your data. It contains none of the kit. It runs on the kit. If
          you stop renting, you keep that bundle and replace the foundation
          at the seams — backend extensions, frontend extensions, and the
          entity registry. A kit update cannot commit into your repo.
        </p>
        <div className="ak-frame">
          <div className="ak-frame-bar">
            <span>how your code stays yours</span>
            <strong>read only</strong>
          </div>
          <KitIpDiagram />
        </div>
        <ul className="ak-own">
          <li>
            <strong>You own</strong>
            The layer repo, your domain content, and the export bundle.
          </li>
          <li>
            <strong>You rent</strong>
            The license: the load path, health checks, hardening, and updates, plus Dev Console on this instance. nQube inside this app is included. Org-wide pipelines are Platform.
          </li>
          <li>
            <strong>You can leave</strong>
            Take the layer and your data. The foundation stays. Moving the server keeps the license.
          </li>
        </ul>
      </section>

      <section className="ak-section">
        <h2>Where the server runs</h2>
        <p className="ak-intro">
          The fee includes a managed host. Run the same instance in your cloud
          or on-prem and the license stays; compute, storage, egress, and GPU
          on our side drop off the bill. Phone, desktop, and the store shells
          still talk to that one instance.
        </p>
        <div className="ak-grid ak-grid-3">
          <article className="ak-card ak-host">
            <div className="ak-host-tag">nventr hosted</div>
            <h3>A host we run</h3>
            <p>We run the stack. TLS, backups, and deploys from the Dev Console are included in the license. This is the default.</p>
          </article>
          <article className="ak-card ak-host">
            <div className="ak-host-tag">Your cloud</div>
            <h3>AWS, Azure, or Google Cloud</h3>
            <p>The same compose file, in your account. Images are signed. You pull them. The license stays. Host meters on our side do not.</p>
          </article>
          <article className="ak-card ak-host">
            <div className="ak-host-tag">On-prem</div>
            <h3>A Linux machine you control</h3>
            <p>The stack runs on hardware you already have. The license stays. The browser and the store shells still reach it over your network.</p>
          </article>
        </div>
      </section>

      <section className="ak-section">
        <h2>What phones home</h2>
        <p className="ak-intro">
          Two channels, both counters and a license check. Content stays on
          the instance. A contracted perpetual license turns both channels off.
        </p>
        <div className="ak-grid ak-grid-2">
          <article className="ak-card">
            <div className="ak-host-tag">Every 12 hours</div>
            <h3>License check</h3>
            <p>
              The instance confirms it is still entitled. Miss the window past
              the grace period and the stack stops. The grace period is tunable
              on the agreement.
            </p>
          </article>
          <article className="ak-card">
            <div className="ak-host-tag">Every 6 hours</div>
            <h3>Usage counters</h3>
            <p>
              Signed counts — runs, hours, storage — leave the box. Documents,
              files, and prompts do not. The signature is HMAC. The payload is
              the numbers.
            </p>
          </article>
        </div>
      </section>

      <section className="ak-cta">
        <h2>Launch the instance</h2>
        <p className="ak-intro">
          {perMonth(appKit)} is that foundation, and Dev Console is part of it. Kit Site
          is {perMonth(kitBuild)} when you only need the public site. Desktop and phone in
          the browser are the product. A store shell would wrap that same
          build later. It is not a separate plan. Org-wide pipelines into
          systems you already use are Platform — a separate cart, or a
          written bundle.
        </p>
        <div className="cs-land-cta-row">
          <Link to={appKit.href} className="cs-btn cs-btn-primary">{appKit.cta}</Link>
          <Link className="cs-btn cs-btn-ghost" to="/contact?subject=nventr%20App%20Kit">Talk to us</Link>
          <Link to="/pricing" className="cs-btn cs-btn-ghost">See pricing</Link>
          <Link to="/platform" className="cs-btn cs-btn-ghost">Platform →</Link>
        </div>
      </section>
    </div>
    </PageWorld>
  );
}
